import React, { useEffect, useState } from 'react';
import { Button, Typography, Box, Grid, useMediaQuery } from '@mui/material';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { useSubscriptionContext } from '../../../context/subscription_context/SubscriptionContext';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../../../utils/enums/CloudFunctions';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import MobileAppSidebar from '../../../components/navbar/MobileAppSidebar';

const Subscription = () => {
    const { organizationStripeInfo } = useOrganizationContext();
    const [customerId, setCustomerId] = useState('');
    const [availableSeats, setAvailableSeats] = useState(0);
    const [usedSeats, setUsedSeats] = useState(0);
    const isMobile = useMediaQuery('(max-width:600px)');
    const { subscription, seatsAvailable, subscribedUsers } = useSubscriptionContext();
    const { hasPermission } = usePermissions();
    const { t } = useTranslation();
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();

    useEffect(() => {
        if (hasPermission(Modules.SUBSCRIPTION, PermissionTypes.WRITE) && organizationStripeInfo) {
            setCustomerId(organizationStripeInfo.stripe_customer_id);
        }
    }, [organizationStripeInfo]);

    useEffect(() => {
        if (subscription) {
            const totalSeats = subscription.quantity;
            const usedSeats = subscribedUsers ? Object.keys(subscribedUsers).length : 0;
            setAvailableSeats(totalSeats);
            setUsedSeats(usedSeats);
        }
    }, [subscription, subscribedUsers]);

    const redirectToCustomerPortal = async () => {
        setLoadingData(true);
        try {
            const functions = getFunctions();
            const createCustomerPortalSession = httpsCallable(functions, CloudFunctions.CreateCustomerPortalSession);
            const response = await createCustomerPortalSession({ customerId });
    
            if (response.data.url) {
                if (window.Android) {
                    // ✅ Open in External Browser for WebView
                    window.Android.openExternalUrl(response.data.url);
                } else {
                    // ✅ Works in regular browser
                    window.location.href = response.data.url;
                }
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };
    

    const subscriptionContent = (
        <>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{mb:2}} component="div">
                {t(StringKeys.SUBSCRIPTION_DETAILS)}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    {subscription ? (
                        <>
                            <Typography>
                                <strong>{t(StringKeys.STATUS)}:</strong> {t(subscription.status)}
                            </Typography>
                            <Typography>
                                <strong>{t(StringKeys.CURRENT_PERIOD_END)}:</strong>{' '}
                                {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                            </Typography>
                            <Typography>
                                <strong>{t(StringKeys.SUBSCRIPTIONS_USED)}:</strong>{' '}
                                {usedSeats} of {availableSeats}
                            </Typography>
                            {hasPermission(Modules.SUBSCRIPTION, PermissionTypes.WRITE) && (
                                <Box mt={3}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={redirectToCustomerPortal}
                                    >
                                        {t(StringKeys.MANAGE_SUBSCRIPTION)}
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography>
                                {t(StringKeys.NO_ACTIVE_SUBSCRIPTION)}
                            </Typography>
                            <Box mt={3}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={redirectToCustomerPortal}
                                >
                                    {t(StringKeys.MANAGE_SUBSCRIPTION)}
                                </Button>
                            </Box>
                        </>
                    )}
                </Grid>
            </Grid>
        </>
    );

    // If Stripe onboarding is not complete, render without AppSidebar
    if (!organizationStripeInfo?.onboarding_complete) {
        return (
            <>
                <MobileAppSidebar/>
                <Box sx={{ marginTop: '80px', padding: '30px' }}>
                    {subscriptionContent}
                </Box>
            </>
        );
    }

    // If Stripe onboarding is complete, render with AppSidebar
    return <AppSidebar content={subscriptionContent} />;
};

export default Subscription;
